import React, {
	CSSProperties,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import IconAddImage from "../../../dynamicicons/icon-add-image";
import VelavuItemIcon from "../../../elements/velavu-item-icon";
import * as styles from "./asset-image-preview.module.scss";
import whitelabel from "../../../whitelabel";
import { AssetCategory } from "velavu-js-api";

interface AssetImagePreviewProps {
	style?: CSSProperties;
	category?: string;
	image?: string;
	setImage?: (url: string) => void;
}

export default function AssetImagePreview(props: AssetImagePreviewProps) {
	const { t } = useTranslation();
	const [image, setImage] = useState<string | undefined>(undefined);
	const imageInput = useRef<HTMLInputElement>(null);

	const onChangeImage = useCallback(
		(event: React.FormEvent<HTMLInputElement>) => {
			const image = event.currentTarget.files![0];
			const imageURL = URL.createObjectURL(image);

			props.setImage?.(imageURL);
		},
		[props],
	);

	const renderImage = useCallback(() => {
		if (image) {
			return <img className={styles.image} src={image} alt="" />;
		} else if (!image && props.category) {
			if (Object.values<string>(AssetCategory).includes(props.category)) {
				return (
					<VelavuItemIcon
						data={{ category: props.category as AssetCategory }}
					/>
				);
			} else {
				//Fallback
				return (
					<VelavuItemIcon
						data={{ category: AssetCategory.Equipment }}
					/>
				);
			}
		} else {
			return (
				<button onClick={() => imageInput.current?.click()}>
					<IconAddImage
						className={styles.iconAddImage}
						color={whitelabel.palette.secondary500}
					/>
				</button>
			);
		}
	}, [image, props.category]);

	useEffect(() => {
		if (!props.image) return;

		setImage(props.image);
	}, [props.image]);

	return (
		<div className={styles.container} style={props.style}>
			<div className={styles.content}>{renderImage()}</div>
			<button
				style={{ marginLeft: 16 }}
				className={styles.imageButton}
				onClick={() => imageInput.current?.click()}
			>
				{image
					? t("settings.user.upload_new_photo")
					: t("settings.user.upload_photo")}
			</button>
			{image && (
				<div>
					<span className={styles.bulletPoint} />
					<button
						className={styles.imageButton}
						onClick={() => setImage(undefined)}
					>
						{t("settings.user.remove_photo")}
					</button>
				</div>
			)}
			<input
				className={styles.imageInput}
				ref={imageInput}
				onChange={onChangeImage}
				type="file"
				name="image"
				accept="image/png, image/jpeg"
			/>
		</div>
	);
}
